import useInView from '../../../hooks/useInView';

interface Testimonial {
  name: string;
  location: string;
  text: string;
}

interface ServiceTestimonialsProps {
  testimonials: Testimonial[];
  serviceTitle: string;
}

const StarRating = () => (
  <div className="flex items-center space-x-0.5 mb-3">
    {[1, 2, 3, 4, 5].map((s) => (
      <div key={s} className="w-4 h-4 flex items-center justify-center">
        <i className="ri-star-fill text-[#F59E0B] text-sm" />
      </div>
    ))}
  </div>
);

const ServiceTestimonials = ({ testimonials, serviceTitle }: ServiceTestimonialsProps) => {
  const { ref, inView } = useInView({ threshold: 0.1 });

  return (
    <section className="py-16 md:py-20 bg-[#F8F9FB]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="text-center mb-12 transition-all duration-700"
          style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <p className="text-[#C41E3A] text-xs font-bold uppercase tracking-widest mb-2">Customer Stories</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#111827]">
            Real {serviceTitle} Customers
          </h2>
          <p className="text-[#6B7280] mt-3 max-w-md mx-auto text-sm sm:text-base">
            Don&apos;t take our word for it — here&apos;s what homeowners across the Gulf Coast are saying.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={t.name}
              className="bg-white rounded-xl p-6 border border-[#E2E8F0] flex flex-col transition-all duration-700"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(32px)',
                transitionDelay: `${i * 130}ms`,
              }}
            >
              <div className="w-8 h-8 flex items-center justify-center mb-3">
                <i className="ri-double-quotes-l text-3xl text-[#C41E3A]/20" />
              </div>
              <StarRating />
              <p className="text-sm text-[#374151] leading-relaxed flex-1 mb-5">&ldquo;{t.text}&rdquo;</p>
              <div className="flex items-center space-x-3 pt-4 border-t border-[#F3F4F6]">
                <div className="w-9 h-9 rounded-full bg-[#C41E3A]/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-[#C41E3A] text-sm" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#111827]">{t.name}</div>
                  <div className="text-xs text-[#9CA3AF]">{t.location}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceTestimonials;
